/**
 * Gestión de Documentos de Empleado
 * Fecha: 16 Oct 2025
 * 
 * Vista completa para gestión de expediente digital con tabs, carga de documentos,
 * aprobación/rechazo y control de versiones
 */
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { employeeDocumentsService } from '@/features/rh/api/employeeDocuments.service';
import { documentTypesService } from '@/features/configuracion/api/documentTypes.service';
import { employeesService } from '@/features/rh/api/employees.service';
import { fileService } from '@/services/fileService';
import type {
  EmployeeDocument,
  DocumentType,
  Employee,
  DocumentSummary,
} from '@/types/employee.types';
import {
  LoadingSpinner,
  Badge,
  DocumentStatusBadge,
  Modal,
  CircularProgress,
  PermissionGuard,
} from '@/shared/components';
import {
  ArrowLeft,
  Upload,
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  History,
  AlertTriangle,
  Calendar,
  Search,
  RefreshCw,
  Clock,
} from 'lucide-react';
import { usePermissions, EMPLOYEE_PERMISSIONS } from '@/shared/hooks/usePermissions';
import { Notification, useNotification } from '@/shared/components/Notification';
import { formatDate } from '@/shared/lib/dateUtils';
import SelectBuscable from '@/shared/components/SelectBuscable';
import KpiCard from '@/features/rh/components/dashboard/KpiCard';

type TabId = 'all' | 'sin_cargar' | 'pendiente' | 'en_revision' | 'aprobado' | 'rechazado' | 'vencido' | 'no_aplica';

interface TabConfig {
  id: TabId;
  label: string;
  count?: number;
  color: string;
}

interface DocumentRow {
  id?: number;
  documentTypeId: number;
  documentType: DocumentType;
  status: 'sin_cargar' | 'pendiente' | 'en_revision' | 'aprobado' | 'rechazado' | 'vencido' | 'no_aplica';
  version?: number;
  fileUrl?: string;
  createdAt?: string;
  fechaVencimiento?: string;
  comentarios?: string;
  isUploaded: boolean;
  isMissing: boolean;
}

export const EmployeeDocuments: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { hasPermission } = usePermissions();
  const { notification, showSuccess, showError, hideNotification } = useNotification();

  const [employee, setEmployee] = useState<Employee | null>(null);
  const [documents, setDocuments] = useState<EmployeeDocument[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DocumentType[]>([]);
  const [summary, setSummary] = useState<DocumentSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Upload Modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDocumentTypeId, setUploadDocumentTypeId] = useState<number | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadExpirationDate, setUploadExpirationDate] = useState('');
  const [uploadObservations, setUploadObservations] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Approve Modal
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveDocument, setApproveDocument] = useState<EmployeeDocument | null>(null);
  const [approveObservations, setApproveObservations] = useState('');
  const [isApproving, setIsApproving] = useState(false);

  // Reject Modal
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectDocument, setRejectDocument] = useState<EmployeeDocument | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // Versions Modal
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [versionsDocument, setVersionsDocument] = useState<EmployeeDocument | null>(null);
  const [versions, setVersions] = useState<EmployeeDocument[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);

  // Document Viewer Modal
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [viewerDocument, setViewerDocument] = useState<EmployeeDocument | null>(null);
  const [viewerFileUrl, setViewerFileUrl] = useState<string | null>(null);
  const [viewerFileError, setViewerFileError] = useState<string | null>(null);
  const [isViewerFileLoading, setIsViewerFileLoading] = useState(false);
  const [viewerAction, setViewerAction] = useState<'approve' | 'reject' | 'revision' | 'expire' | null>(null);
  const [viewerObservations, setViewerObservations] = useState('');
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Not Applicable Modal
  const [showNotApplicableModal, setShowNotApplicableModal] = useState(false);
  const [notApplicableDocumentType, setNotApplicableDocumentType] = useState<DocumentType | null>(null);
  const [notApplicableReason, setNotApplicableReason] = useState('');
  const [isMarkingNotApplicable, setIsMarkingNotApplicable] = useState(false);

  const canApprove = hasPermission(EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_APPROVE);
  const canReject = hasPermission(EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_DELETE);

  const normalizeText = (value?: string | null): string =>
    (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const isCvDocumentType = (documentType?: DocumentType | null): boolean => {
    const normalizedName = normalizeText(documentType?.nombre);
    return normalizedName.includes('curriculum') || normalizedName === 'cv';
  };

  const isValidFileUrl = (fileUrl?: string | null): boolean => {
    if (typeof fileUrl !== 'string') return false;
    const normalized = fileUrl.trim();
    if (!normalized) return false;

    const invalidTokens = new Set(['null', 'undefined', 'n/a', 'na', '-']);
    return !invalidTokens.has(normalized.toLowerCase());
  };

  const isDocumentRequired = (documentType?: DocumentType | null): boolean => {
    if (!documentType) return false;
    if (isCvDocumentType(documentType) && employee?.cvRequired === false) {
      return false;
    }
    return Boolean(documentType.esObligatorio);
  };

  const hasDocumentFile = (doc: DocumentRow): boolean =>
    isValidFileUrl(doc.fileUrl);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  useEffect(() => {
    return () => {
      fileService.revokeViewableFileUrl(viewerFileUrl);
    };
  }, [viewerFileUrl]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [employeeData, documentsData, typesData, summaryData] = await Promise.all([
        employeesService.getById(Number(id)),
        employeeDocumentsService.getAll({ employeeId: Number(id) }),
        documentTypesService.getAll(),
        employeeDocumentsService.getSummary(Number(id)),
      ]);
      setEmployee(employeeData);
      setDocuments(documentsData);
      setDocumentTypes(typesData.filter((dt) => dt.activo));
      setSummary(summaryData);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar datos:', err);
      showError('Error al cargar datos', err.message || 'No se pudieron cargar los documentos del empleado');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    setUploadDocumentTypeId(null);
    setUploadFile(null);
    setUploadExpirationDate('');
    setUploadObservations('');
    setShowUploadModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setUploadFile(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setUploadFile(e.dataTransfer.files[0]);
    }
  };

  const handleUploadSubmit = async () => {
    if (!uploadDocumentTypeId || !uploadFile) {
      showError('Datos incompletos', 'Por favor selecciona un tipo de documento y un archivo');
      return;
    }

    try {
      setIsUploading(true);
      await employeeDocumentsService.upload(uploadFile, {
        employeeId: Number(id),
        documentTypeId: uploadDocumentTypeId,
        comentarios: uploadObservations || undefined,
        fechaVencimiento: uploadExpirationDate || undefined,
      });
      await loadData();
      
      // Cerrar modal y limpiar antes de mostrar éxito
      setShowUploadModal(false);
      setUploadDocumentTypeId(null);
      setUploadFile(null);
      setUploadExpirationDate('');
      setUploadObservations('');
      
      showSuccess('Documento cargado', 'El documento se ha cargado correctamente');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar documento:', err);
      
      // Cerrar modal primero para que la notificación sea visible
      setShowUploadModal(false);
      
      // Procesar mensaje de error (puede ser string o array)
      let errorMessage = 'No se pudo cargar el documento';
      if (err.message) {
        if (Array.isArray(err.message)) {
          // Traducir mensajes comunes del backend
          errorMessage = err.message.map((msg: string) => {
            if (msg.includes('employeeId must be an integer')) {
              return 'El ID del empleado debe ser un número entero';
            }
            if (msg.includes('documentTypeId must be an integer')) {
              return 'El ID del tipo de documento debe ser un número entero';
            }
            return msg;
          }).join(', ');
        } else {
          errorMessage = err.message;
        }
      }
      
      showError('Error al cargar documento', errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleApproveClick = (doc: EmployeeDocument) => {
    setApproveDocument(doc);
    setApproveObservations('');
    setShowApproveModal(true);
  };

  const handleApproveSubmit = async () => {
    if (!approveDocument) return;
    try {
      setIsApproving(true);
      await employeeDocumentsService.approve(approveDocument.id, approveObservations);
      await loadData();
      setShowApproveModal(false);
      setApproveDocument(null);
      setApproveObservations('');
      showSuccess('Documento aprobado', 'El documento ha sido aprobado correctamente');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al aprobar documento:', err);
      showError('Error al aprobar documento', err.message || 'No se pudo aprobar el documento');
    } finally {
      setIsApproving(false);
    }
  };

  const handleRejectClick = (doc: EmployeeDocument) => {
    setRejectDocument(doc);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectDocument || !rejectReason.trim()) {
      showError('Motivo requerido', 'Debes proporcionar un motivo de rechazo');
      return;
    }
    try {
      setIsRejecting(true);
      await employeeDocumentsService.reject(rejectDocument.id, rejectReason);
      await loadData();
      setShowRejectModal(false);
      setRejectDocument(null);
      setRejectReason('');
      showSuccess('Documento rechazado', 'El documento ha sido rechazado correctamente');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al rechazar documento:', err);
      showError('Error al rechazar documento', err.message || 'No se pudo rechazar el documento');
    } finally {
      setIsRejecting(false);
    }
  };

  const handleViewVersions = async (doc: EmployeeDocument) => {
    if (!doc.documentTypeId) return;
    try {
      setVersionsDocument(doc);
      setShowVersionsModal(true);
      setIsLoadingVersions(true);
      const versionsData = await employeeDocumentsService.getVersions(Number(id), doc.documentTypeId);
      setVersions(versionsData);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar versiones:', err);
      showError('Error al cargar versiones', err.message || 'No se pudieron cargar las versiones del documento');
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const closeViewerModal = () => {
    setShowViewerModal(false);
    setViewerDocument(null);
    setViewerAction(null);
    setViewerObservations('');
    setViewerFileError(null);
    setIsViewerFileLoading(false);
    fileService.revokeViewableFileUrl(viewerFileUrl);
    setViewerFileUrl(null);
  };

  const handleOpenViewer = async (doc: EmployeeDocument) => {
    setViewerDocument(doc);
    setViewerAction(null);
    setViewerObservations('');
    setViewerFileError(null);
    setIsViewerFileLoading(true);
    fileService.revokeViewableFileUrl(viewerFileUrl);
    setViewerFileUrl(null);
    setShowViewerModal(true);

    if (!isValidFileUrl(doc.fileUrl)) {
      setViewerFileError('No hay archivo disponible para este documento.');
      setIsViewerFileLoading(false);
      return;
    }

    try {
      const blobUrl = await fileService.getViewableFileUrl(doc.fileUrl);
      setViewerFileUrl(blobUrl);
    } catch (err) {
      console.error('Error al cargar visor de documento:', err);
      setViewerFileError(
        'No fue posible visualizar el documento en este momento. Verifica permisos o intenta más tarde.'
      );
    } finally {
      setIsViewerFileLoading(false);
    }
  };

  const handleViewerActionSubmit = async () => {
    if (!viewerDocument || !viewerAction) return;

    try {
      setIsProcessingAction(true);
      
      switch (viewerAction) {
        case 'approve':
          await employeeDocumentsService.approve(viewerDocument.id, viewerObservations);
          showSuccess('Documento aprobado', 'El documento ha sido aprobado correctamente');
          break;
        case 'reject':
          if (!viewerObservations.trim()) {
            showError('Motivo requerido', 'Debes proporcionar un motivo de rechazo');
            return;
          }
          await employeeDocumentsService.reject(viewerDocument.id, viewerObservations);
          showSuccess('Documento rechazado', 'El documento ha sido rechazado correctamente');
          break;
        case 'revision':
          await employeeDocumentsService.sendToReview(viewerDocument.id, viewerObservations);
          showSuccess('Enviado a revisión', 'El documento ha sido enviado a revisión');
          break;
        case 'expire':
          await employeeDocumentsService.markAsExpired(viewerDocument.id, viewerObservations);
          showSuccess('Documento vencido', 'El documento ha sido marcado como vencido');
          break;
      }
      
      await loadData();
      closeViewerModal();
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al procesar acción:', err);
      showError('Error', err.message || 'No se pudo procesar la acción');
    } finally {
      setIsProcessingAction(false);
    }
  };

  const handleDownload = async (doc: EmployeeDocument) => {
    try {
      if (!isValidFileUrl(doc.fileUrl)) {
        showError('Archivo no disponible', 'Este documento no tiene un archivo asociado');
        return;
      }

      await fileService.downloadFile(
        doc.fileUrl,
        `${doc.documentType?.nombre || 'documento'}_${doc.version || 'v1'}`
      );
      
      showSuccess('Descarga iniciada', 'El documento se está descargando');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al descargar documento:', err);
      showError('Error al descargar', err.message || 'No se pudo descargar el documento');
    }
  };

  const handleOpenNotApplicable = (docType: DocumentType) => {
    setNotApplicableDocumentType(docType);
    setNotApplicableReason('');
    setShowNotApplicableModal(true);
  };

  const handleMarkAsNotApplicable = async () => {
    if (!notApplicableDocumentType || !id) return;

    try {
      setIsMarkingNotApplicable(true);
      await employeeDocumentsService.markAsNotApplicable(
        Number(id),
        notApplicableDocumentType.id,
        notApplicableReason
      );
      showSuccess(
        'Marcado como No Aplica',
        `El documento "${notApplicableDocumentType.nombre}" ha sido marcado como no aplicable`
      );
      await loadData();
      setShowNotApplicableModal(false);
      setNotApplicableDocumentType(null);
      setNotApplicableReason('');
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al marcar como no aplica:', err);
      showError('Error', err.message || 'No se pudo marcar el documento como no aplicable');
    } finally {
      setIsMarkingNotApplicable(false);
    }
  };

  const handleView = (doc: EmployeeDocument) => {
    handleOpenViewer(doc);
  };

  /**
   * Combina tipos de documentos con documentos cargados
   */
  const getAllDocumentRows = (): DocumentRow[] => {
    const rows: DocumentRow[] = [];
    
    documentTypes.forEach(docType => {
      const uploadedDoc = documents.find(doc => doc.documentTypeId === docType.id);
      
      if (uploadedDoc) {
        rows.push({
          ...uploadedDoc,
          documentType: docType,
          isUploaded: true,
          isMissing: false
        } as DocumentRow);
      } else {
        rows.push({
          documentTypeId: docType.id,
          documentType: docType,
          status: 'sin_cargar',
          isUploaded: false,
          isMissing: isDocumentRequired(docType)
        });
      }
    });
    
    return rows;
  };

  const getFilteredDocuments = (): DocumentRow[] => {
    let filtered = getAllDocumentRows();

    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter((doc) => doc.status === activeTab);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (doc) =>
          doc.documentType?.nombre.toLowerCase().includes(term) ||
          doc.documentType?.categoria.toLowerCase().includes(term) ||
          doc.comentarios?.toLowerCase().includes(term)
      );
    }

    // Filter by category
    if (selectedCategory) {
      filtered = filtered.filter((doc) => doc.documentType?.categoria === selectedCategory);
    }

    return filtered;
  };

  const getCategories = (): string[] => {
    const categories = new Set(documentTypes.map((dt) => dt.categoria));
    return Array.from(categories).sort();
  };

  /**
   * Obtener tipos de documentos que no tienen documento cargado
   */

  /**
   * Obtener tipos de documentos disponibles para subir (excluyendo los que ya tienen documentos)
   */
  const getAvailableDocumentTypes = (): DocumentType[] => {
    return documentTypes.filter((docType) => {
      // Excluir tipos de documentos que ya tienen un documento subido (sin importar el estado)
      const hasDocument = documents.some(
        (doc) => doc.documentTypeId === docType.id && doc.status !== 'no_aplica'
      );
      return !hasDocument;
    });
  };

  const allRows = getAllDocumentRows();
  
  const tabs: TabConfig[] = [
    { id: 'all', label: 'Todos', count: allRows.length, color: 'blue' },
    {
      id: 'sin_cargar',
      label: 'Sin Cargar',
      count: allRows.filter((d) => d.status === 'sin_cargar').length,
      color: 'gray',
    },
    {
      id: 'pendiente',
      label: 'Pendientes',
      count: allRows.filter((d) => d.status === 'pendiente').length,
      color: 'gray',
    },
    {
      id: 'en_revision',
      label: 'En Revisión',
      count: allRows.filter((d) => d.status === 'en_revision').length,
      color: 'yellow',
    },
    {
      id: 'aprobado',
      label: 'Aprobados',
      count: allRows.filter((d) => d.status === 'aprobado').length,
      color: 'green',
    },
    {
      id: 'rechazado',
      label: 'Rechazados',
      count: allRows.filter((d) => d.status === 'rechazado').length,
      color: 'red',
    },
    {
      id: 'vencido',
      label: 'Vencidos',
      count: allRows.filter((d) => d.status === 'vencido').length,
      color: 'orange',
    },
    {
      id: 'no_aplica',
      label: 'No Aplica',
      count: allRows.filter((d) => d.status === 'no_aplica').length,
      color: 'gray',
    },
  ];

  const filteredDocuments = getFilteredDocuments();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Cargando documentos..." />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-6 shadow-sm">
            <h3 className="text-lg font-bold text-red-700 mb-2">
              Error al cargar empleado
            </h3>
            <button
              onClick={() => navigate('/employees')}
              className={estiloDeBoton('primario')}
            >
              Volver a la lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/employees/${id}`)}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al detalle del empleado
          </button>
          <div className="flex items-center justify-between">
            <div>
              <TituloDePantalla nivel="interna" className="mb-2">
                Expediente Digital
              </TituloDePantalla>
              <p className="text-gray-600 dark:text-gray-400">
                {employee.user?.firstName} {employee.user?.lastName}
              </p>
            </div>
            <PermissionGuard permission={EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_CREATE}>
              <button
                onClick={handleUploadClick}
                className={estiloDeBoton('primario')}
              >
                <Upload className="w-4 h-4" />
                Cargar Documento
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Completitud</span>
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <CircularProgress value={employee.completenessPercentage} size={80} showLabel />
            </div>

            <KpiCard title="Total" value={summary.total} subtext="documentos" icon={<FileText />} variant="primary" />
            <KpiCard title="Aprobados" value={summary.aprobados} subtext="documentos" icon={<CheckCircle />} variant="success" />
            <KpiCard title="Pendientes" value={summary.pendientes + summary.enRevision} subtext="por revisar" icon={<AlertTriangle />} variant="warning" />
          </div>
        )}



        {/* Main Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {tab.label}
                  {tab.count !== undefined && (
                    <Badge color="morado" className="ml-2">
                      {tab.count}
                    </Badge>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar documentos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="sm:w-64">
                <SelectBuscable
                  variante="listado"
                  etiqueta="Categoría"
                  todos="Todas las categorías"
                  ancho="completo"
                  opciones={getCategories().map((cat) => ({ value: cat, label: cat }))}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  canDelete={false}
                  placeholderDeBusqueda="Buscar categoría..."
                />
              </div>
              <button
                onClick={loadData}
                className={estiloDeBoton('secundario')}
              >
                <RefreshCw className="w-4 h-4" />
                Actualizar
              </button>
            </div>
          </div>

          {/* Documents Table */}
          <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Documento
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                    Obligatorio
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Estado
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Versión
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Fecha de Carga
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Vencimiento
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {filteredDocuments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No se encontraron documentos</p>
                    </td>
                  </tr>
                ) : (
                  filteredDocuments.map((doc) => {
                    const hasFile = hasDocumentFile(doc);
                    const isCvWithoutFile = isCvDocumentType(doc.documentType) && !hasFile;
                    return (
                    <tr key={doc.id || `missing-${doc.documentTypeId}`} className="hover:bg-[#F4F7FB] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-start gap-3">
                          <FileText className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            doc.status === 'sin_cargar' ? 'text-gray-300' : 'text-gray-400'
                          }`} />
                          <div>
                            <p className={`text-sm font-medium ${
                              doc.status === 'sin_cargar' 
                                ? 'text-gray-500 dark:text-gray-400' 
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {doc.documentType?.nombre || 'Documento'}
                            </p>
                            {doc.comentarios && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {doc.comentarios}
                              </p>
                            )}
                            {isCvWithoutFile && (
                              <p className="text-xs text-amber-600 mt-1">
                                Sin archivo disponible
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge color="morado">
                          {doc.documentType?.categoria || '-'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {isDocumentRequired(doc.documentType) ? (
                          <Badge color="rojo">
                            Sí
                          </Badge>
                        ) : (
                          <Badge color="gris">
                            No
                          </Badge>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {doc.status === 'sin_cargar' ? (
                          <Badge color="gris">
                            Sin cargar
                          </Badge>
                        ) : (
                          <DocumentStatusBadge status={doc.status} />
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {doc.version ? `v${doc.version}` : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {doc.createdAt ? (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(doc.createdAt).toLocaleDateString('es-MX')}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {hasFile && doc.fechaVencimiento ? (
                          <span
                            className={
                              doc.status === 'vencido'
                                ? 'text-red-600 dark:text-red-400 font-medium'
                                : ''
                            }
                          >
                            {formatDate(doc.fechaVencimiento)}
                          </span>
                        ) : (
                          <span className="text-gray-400">-----</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {doc.status === 'sin_cargar' ? (
                            <PermissionGuard permission={EMPLOYEE_PERMISSIONS.EMPLOYEE_DOCUMENTS_CREATE}>
                              <>
                                <button
                                  onClick={() => {
                                    setUploadDocumentTypeId(doc.documentTypeId);
                                    setShowUploadModal(true);
                                  }}
                                  className={estiloDeBoton('primario')}
                                  title="Subir documento"
                                >
                                  <Upload className="w-4 h-4" />
                                  Subir
                                </button>
                                <button
                                  onClick={() => handleOpenNotApplicable(doc.documentType)}
                                  className={estiloDeBoton('secundario')}
                                  title="Marcar como No Aplica"
                                >
                                  N/A
                                </button>
                              </>
                            </PermissionGuard>
                          ) : (
                            <>
                              {doc.status !== 'no_aplica' && (
                                <>
                                  <button
                                    onClick={() => doc.id && handleView(doc as unknown as EmployeeDocument)}
                                    className="p-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                    title="Ver documento"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </button>
                                  {hasFile && (
                                    <button
                                      onClick={() => doc.id && handleDownload(doc as unknown as EmployeeDocument)}
                                      className="p-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                      title="Descargar"
                                    >
                                      <Download className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                              {hasFile && (
                                <button
                                  onClick={() => doc.id && handleViewVersions(doc as unknown as EmployeeDocument)}
                                  className="p-1 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                  title="Ver historial de versiones"
                                >
                                  <History className="w-4 h-4" />
                                </button>
                              )}
                              {canApprove && doc.status === 'en_revision' && (
                                <button
                                  onClick={() => doc.id && handleApproveClick(doc as unknown as EmployeeDocument)}
                                  className="p-1 text-green-600 dark:text-green-400 hover:text-green-700"
                                  title="Aprobar"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              {canReject && doc.status === 'en_revision' && (
                                <button
                                  onClick={() => doc.id && handleRejectClick(doc as unknown as EmployeeDocument)}
                                  className="p-1 text-red-600 dark:text-red-400 hover:text-red-700"
                                  title="Rechazar"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setUploadDocumentTypeId(null);
          setUploadFile(null);
          setUploadExpirationDate('');
          setUploadObservations('');
        }}
        title="Cargar Documento"
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Tipo de Documento *
            </label>
            <SelectBuscable
              variante="formulario"
              opciones={getAvailableDocumentTypes().map((dt) => ({
                value: String(dt.id),
                label: `${dt.nombre} ${isDocumentRequired(dt) ? '(Obligatorio)' : ''}`.trim(),
              }))}
              value={uploadDocumentTypeId ? String(uploadDocumentTypeId) : ''}
              onChange={(valor) => setUploadDocumentTypeId(valor === '' ? null : Number(valor))}
              placeholder="Seleccionar tipo..."
              placeholderDeBusqueda="Buscar tipo de documento..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Archivo *
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragging
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <Upload className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              {uploadFile ? (
                <div>
                  <p className="text-sm text-gray-900 dark:text-white font-medium mb-1">
                    {uploadFile.name}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {(uploadFile.size / 1024).toFixed(2)} KB
                  </p>
                  <button
                    onClick={() => setUploadFile(null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-700"
                  >
                    Eliminar
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Arrastra un archivo aquí o haz clic para seleccionar
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={estiloDeBoton('primario')}
                  >
                    Seleccionar archivo
                  </button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    PDF, Word o imagen (Máx. 10MB)
                  </p>
                </div>
              )}
            </div>
          </div>

          {uploadDocumentTypeId &&
            documentTypes.find((dt) => dt.id === uploadDocumentTypeId)?.tieneVencimiento && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Fecha de Vencimiento
                </label>
                <input
                  type="date"
                  value={uploadExpirationDate}
                  onChange={(e) => setUploadExpirationDate(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Observaciones
            </label>
            <textarea
              value={uploadObservations}
              onChange={(e) => setUploadObservations(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              placeholder="Notas adicionales sobre el documento..."
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setShowUploadModal(false)}
            className={estiloDeBoton('secundario')}
            disabled={isUploading}
          >
            Cancelar
          </button>
          <button
            onClick={handleUploadSubmit}
            disabled={isUploading || !uploadDocumentTypeId || !uploadFile}
            className={estiloDeBoton('primario')}
          >
            {isUploading && <LoadingSpinner size="sm" />}
            Cargar Documento
          </button>
        </div>
      </Modal>

      {/* Approve Modal */}
      <Modal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        title="Aprobar Documento"
      >
        {approveDocument && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Documento</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {approveDocument.documentType?.nombre || 'Documento'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Observaciones (opcional)
              </label>
              <textarea
                value={approveObservations}
                onChange={(e) => setApproveObservations(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Notas adicionales sobre la aprobación..."
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setShowApproveModal(false)}
            className={estiloDeBoton('secundario')}
            disabled={isApproving}
          >
            Cancelar
          </button>
          <button
            onClick={handleApproveSubmit}
            disabled={isApproving}
            className={estiloDeBoton('exito')}
          >
            {isApproving && <LoadingSpinner size="sm" />}
            <CheckCircle className="w-4 h-4" />
            Aprobar Documento
          </button>
        </div>
      </Modal>

      {/* Reject Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="Rechazar Documento"
      >
        {rejectDocument && (
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 shadow-sm">
              <p className="text-sm text-red-600 font-semibold mb-1">Documento</p>
              <p className="font-bold text-red-700">
                {rejectDocument.documentType?.nombre || 'Documento'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivo de Rechazo *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                placeholder="Explica por qué se rechaza este documento..."
              />
            </div>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={() => setShowRejectModal(false)}
            className={estiloDeBoton('secundario')}
            disabled={isRejecting}
          >
            Cancelar
          </button>
          <button
            onClick={handleRejectSubmit}
            disabled={isRejecting || !rejectReason.trim()}
            className={estiloDeBoton('peligro')}
          >
            {isRejecting && <LoadingSpinner size="sm" />}
            <XCircle className="w-4 h-4" />
            Rechazar Documento
          </button>
        </div>
      </Modal>

      {/* Versions Modal */}
      <Modal
        isOpen={showVersionsModal}
        onClose={() => setShowVersionsModal(false)}
        title="Historial de Versiones"
        size="lg"
      >
        {versionsDocument && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Documento</p>
              <p className="font-medium text-gray-900 dark:text-white">
                {versionsDocument.documentType?.nombre || 'Documento'}
              </p>
            </div>

            {isLoadingVersions ? (
              <div className="py-8 flex justify-center">
                <LoadingSpinner size="md" text="Cargando versiones..." />
              </div>
            ) : versions.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                No hay versiones anteriores
              </p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {versions.map((version) => (
                  <div
                    key={version.id}
                    className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Versión {version.version}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(version.createdAt).toLocaleString('es-MX')}
                        </p>
                      </div>
                      <DocumentStatusBadge status={version.status} />
                    </div>
                    {version.comentarios && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                        {version.comentarios}
                      </p>
                    )}
                    {/* Solo mostrar botones Ver/Descargar si NO es "no_aplica" */}
                    {version.status !== 'no_aplica' &&
                      isValidFileUrl(version.fileUrl) && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleView(version)}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Eye className="w-3 h-3" />
                          Ver
                        </button>
                        <button
                          onClick={() => handleDownload(version)}
                          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                        >
                          <Download className="w-3 h-3" />
                          Descargar
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={() => setShowVersionsModal(false)}
            className={estiloDeBoton('secundario')}
          >
            Cerrar
          </button>
        </div>
      </Modal>

      {/* Document Viewer Modal */}
      <Modal
        isOpen={showViewerModal}
        onClose={closeViewerModal}
        title="Visor de Documento"
        size="2xl"
      >
        {viewerDocument && (
          <div className="space-y-6">
            {/* Document Info */}
            <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {viewerDocument.documentType?.nombre || 'Documento'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Versión {viewerDocument.version || 'v1'} • {viewerDocument.documentType?.categoria}
                  </p>
                </div>
                <DocumentStatusBadge status={viewerDocument.status} />
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {isViewerFileLoading ? (
                <div className="w-full h-96 flex items-center justify-center">
                  <LoadingSpinner text="Cargando documento..." />
                </div>
              ) : viewerFileError ? (
                <div className="w-full h-96 flex items-center justify-center px-6 text-center">
                  <div>
                    <p className="text-sm font-medium text-red-600">{viewerFileError}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      Si el problema persiste, valida permisos de acceso al documento.
                    </p>
                  </div>
                </div>
              ) : viewerFileUrl ? (
                <iframe
                  src={viewerFileUrl}
                  className="w-full h-96"
                  title="Visor de PDF"
                />
              ) : (
                <div className="w-full h-96 flex items-center justify-center px-6 text-center">
                  <p className="text-sm text-gray-500">No hay archivo disponible para visualizar.</p>
                </div>
              )}
            </div>

            {/* Action Selection */}
            {!viewerAction && (canApprove || canReject) && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {canApprove && (
                  <>
                    <button
                      onClick={() => setViewerAction('approve')}
                      className="flex flex-col items-center gap-2 p-4 border-2 border-green-500 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                    >
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-400">
                        Aprobar
                      </span>
                    </button>

                    <button
                      onClick={() => setViewerAction('revision')}
                      className="flex flex-col items-center gap-2 p-4 border-2 border-yellow-500 rounded-lg hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
                    >
                      <Clock className="w-6 h-6 text-yellow-600" />
                      <span className="text-sm font-medium text-yellow-700 dark:text-yellow-400">
                        A Revisión
                      </span>
                    </button>

                    <button
                      onClick={() => setViewerAction('expire')}
                      className="flex flex-col items-center gap-2 p-4 border-2 border-orange-500 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
                    >
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                      <span className="text-sm font-medium text-orange-700 dark:text-orange-400">
                        Vencido
                      </span>
                    </button>
                  </>
                )}

                {canReject && (
                  <button
                    onClick={() => setViewerAction('reject')}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <XCircle className="w-6 h-6 text-red-600" />
                    <span className="text-sm font-medium text-red-700 dark:text-red-400">
                      Rechazar
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* Action Form */}
            {viewerAction && (
              <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <div className="flex items-center gap-2">
                  {viewerAction === 'approve' && (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h3 className="font-medium text-green-700 dark:text-green-400">
                        Aprobar Documento
                      </h3>
                    </>
                  )}
                  {viewerAction === 'reject' && (
                    <>
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h3 className="font-medium text-red-700 dark:text-red-400">
                        Rechazar Documento
                      </h3>
                    </>
                  )}
                  {viewerAction === 'revision' && (
                    <>
                      <Clock className="w-5 h-5 text-yellow-600" />
                      <h3 className="font-medium text-yellow-700 dark:text-yellow-400">
                        Enviar a Revisión
                      </h3>
                    </>
                  )}
                  {viewerAction === 'expire' && (
                    <>
                      <AlertTriangle className="w-5 h-5 text-orange-600" />
                      <h3 className="font-medium text-orange-700 dark:text-orange-400">
                        Marcar como Vencido
                      </h3>
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {viewerAction === 'reject' ? 'Motivo de rechazo *' : 'Observaciones (opcional)'}
                  </label>
                  <textarea
                    value={viewerObservations}
                    onChange={(e) => setViewerObservations(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    placeholder={
                      viewerAction === 'reject'
                        ? 'Explica por qué se rechaza este documento...'
                        : 'Agrega comentarios adicionales...'
                    }
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setViewerAction(null);
                      setViewerObservations('');
                    }}
                    className={estiloDeBoton('secundario', 'flex-1')}
                    disabled={isProcessingAction}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleViewerActionSubmit}
                    disabled={isProcessingAction || (viewerAction === 'reject' && !viewerObservations.trim())}
                    className={estiloDeBoton(viewerAction === 'approve' ? 'exito' : viewerAction === 'reject' ? 'peligro' : 'primario', 'flex-1')}
                  >
                    {isProcessingAction && <LoadingSpinner size="sm" />}
                    Confirmar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <button
            onClick={() => {
              setShowViewerModal(false);
              setViewerAction(null);
              setViewerObservations('');
            }}
            className={estiloDeBoton('secundario')}
            disabled={isProcessingAction}
          >
            Cerrar
          </button>
        </div>
      </Modal>

      {/* Not Applicable Modal */}
      <Modal
        isOpen={showNotApplicableModal}
        onClose={() => {
          setShowNotApplicableModal(false);
          setNotApplicableDocumentType(null);
          setNotApplicableReason('');
        }}
        title="Marcar Documento como No Aplicable"
        size="lg"
      >
        <div className="space-y-4">
          <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-700 dark:text-blue-400 font-medium">
                  {notApplicableDocumentType?.nombre}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">
                  {notApplicableDocumentType?.categoria}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Al marcar este documento como <strong>"No Aplica"</strong>, el sistema lo registrará
              sin necesidad de archivo. Esta acción es útil cuando el documento no es requerido
              para este empleado en particular.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Motivo (opcional)
            </label>
            <textarea
              value={notApplicableReason}
              onChange={(e) => setNotApplicableReason(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              placeholder="Ej: El empleado no requiere este documento por su tipo de contrato..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                setShowNotApplicableModal(false);
                setNotApplicableDocumentType(null);
                setNotApplicableReason('');
              }}
              className={estiloDeBoton('secundario')}
              disabled={isMarkingNotApplicable}
            >
              Cancelar
            </button>
            <button
              onClick={handleMarkAsNotApplicable}
              className={estiloDeBoton('primario')}
              disabled={isMarkingNotApplicable}
            >
              {isMarkingNotApplicable ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Marcando...
                </>
              ) : (
                'Marcar como N/A'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Notification Component */}
      <Notification
        type={notification.type}
        title={notification.title}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </div>
  );
};
